import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, Star, ChevronRight, Lock } from 'lucide-react';

// Quiz questions data
const quizQuestions = [
  {
    id: 1,
    question: "What stage is your IP/story idea currently in?",
    type: "multiple",
    options: [
      "Just an idea in my head",
      "Outlined but not developed",
      "Partially developed (script/comic/treatment exists)",
      "Fully developed but not monetized",
      "Already monetized but want to scale"
    ]
  },
  {
    id: 2,
    question: "If this IP reaches its full potential, what does that look like to you?",
    type: "multiple",
    options: [
      "Streaming series/film deal",
      "Multi-platform franchise (games, merch, comics)",
      "Web3/NFT collectible ecosystem",
      "Licensing deals and passive royalties",
      "All of the above"
    ]
  },
  {
    id: 3,
    question: "What's the biggest obstacle preventing you from getting there?",
    type: "multiple",
    options: [
      "Don't know how to pitch/package it properly",
      "No connections to studios/investors",
      "Can't afford traditional development costs",
      "Don't understand how to use AI to accelerate production",
      "Not sure if my IP is actually franchiseable"
    ]
  },
  {
    id: 4,
    question: "When you think about why that keeps you stuck, what's really going on?",
    type: "text",
    placeholder: "Share your thoughts in 2-3 sentences..."
  },
  {
    id: 5,
    question: "If nothing changes in the next 6 months, where does your IP end up?",
    type: "multiple",
    options: [
      "Still just an idea",
      "Developed by someone else who moved faster",
      "Stuck in outdated development processes",
      "Losing relevance as AI-native IP takes over",
      "Exactly where it is now"
    ]
  }
];

// Landing Page Component
const LandingPage = ({ onStartQuiz }) => {
  return (
    <div className="min-h-screen bg-[#08090d] text-white overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes subtleFloat {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.7; }
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-headline {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        .animate-subheadline {
          animation: fadeInUp 0.8s ease-out 0.15s forwards;
          opacity: 0;
        }
        .animate-cta {
          animation: fadeInUp 0.8s ease-out 0.3s forwards;
          opacity: 0;
        }
        .animate-reveal-1 { animation: fadeIn 0.6s ease-out 0.1s forwards; opacity: 0; }
        .animate-reveal-2 { animation: fadeIn 0.6s ease-out 0.2s forwards; opacity: 0; }
        .animate-reveal-3 { animation: fadeIn 0.6s ease-out 0.3s forwards; opacity: 0; }
        .bg-animated {
          animation: gradientShift 12s ease infinite;
          background-size: 200% 200%;
        }
        .particle-overlay::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 30% 20%, rgba(60, 109, 240, 0.03) 0%, transparent 50%),
                      radial-gradient(circle at 70% 80%, rgba(198, 168, 94, 0.02) 0%, transparent 50%);
          animation: subtleFloat 10s ease-in-out infinite;
          pointer-events: none;
        }
        .glass-panel {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }
        .premium-button {
          background: linear-gradient(135deg, #C6A85E, #E5C77A);
          box-shadow: 0 8px 24px rgba(198, 168, 94, 0.25);
          transition: all 220ms ease;
        }
        .premium-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(198, 168, 94, 0.35);
          background: linear-gradient(135deg, #D4B56A, #F0D488);
        }
        .serif-heading {
          font-family: 'Playfair Display', Georgia, serif;
          letter-spacing: -0.01em;
        }
        .number-circle {
          border: 1px solid rgba(60, 109, 240, 0.6);
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden particle-overlay">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_6ff09686-3a90-4ae2-8c29-5f23b98b61ac/artifacts/pa5aw5zl_501a43c6-ebc1-4e4c-8925-f3d41f9ba1f2.png"
            alt="Creative network"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#08090d]/80 via-[#08090d]/60 to-[#08090d] bg-animated" />
        </div>
        
        <div className="relative z-10 max-w-[1100px] mx-auto px-6 text-center py-[120px]">
          <h1 className="serif-heading text-[48px] md:text-[56px] lg:text-[68px] font-normal leading-[1.05] mb-0 animate-headline">
            What's <em className="italic font-medium">Really</em> Blocking Your IP<br />
            From Becoming a Franchise?
          </h1>
          <p className="text-[18px] md:text-[20px] text-white/70 mt-6 animate-subheadline">
            (Find Out in 90 Seconds)
          </p>
          <div className="mt-10 animate-cta">
            <button
              onClick={onStartQuiz}
              data-testid="start-quiz-btn"
              className="premium-button inline-flex items-center gap-3 text-[#0F1115] px-9 py-4 text-[15px] font-semibold tracking-[0.1em] rounded-md"
            >
              FIND OUT NOW
              <ChevronRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-[100px] px-6">
        <div className="max-w-[1100px] mx-auto">
          <h2 className="serif-heading text-[32px] md:text-[36px] font-normal text-center text-white/90">In 90 Seconds You'll Discover...</h2>
          <div className="grid md:grid-cols-3 gap-[60px] mt-16">
            <div className="text-center animate-reveal-1">
              <div className="number-circle w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5">
                <span className="text-[rgba(60,109,240,0.9)] text-[14px] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>1</span>
              </div>
              <p className="text-[18px] text-white/90 font-medium mb-2">Your Hidden Bottleneck</p>
              <p className="text-[15px] text-white/65 leading-relaxed">What's holding your IP from scaling to hundreds of locations, consistently.</p>
            </div>
            <div className="text-center animate-reveal-2">
              <div className="number-circle w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5">
                <span className="text-[rgba(60,109,240,0.9)] text-[14px] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>2</span>
              </div>
              <p className="text-[18px] text-white/90 font-medium mb-2">The Investment Barrier</p>
              <p className="text-[15px] text-white/65 leading-relaxed">The #1 bottleneck keeping investors and partners away from your brand.</p>
            </div>
            <div className="text-center animate-reveal-3">
              <div className="number-circle w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5">
                <span className="text-[rgba(60,109,240,0.9)] text-[14px] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>3</span>
              </div>
              <p className="text-[18px] text-white/90 font-medium mb-2">Your Next Move</p>
              <p className="text-[15px] text-white/65 leading-relaxed">The exact next step to accelerate growth <span className="text-white/50">(hint: it's not what most "consultants" tell you)</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-[80px] px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="glass-panel rounded-xl p-10 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_premium-commercials/artifacts/g6qdgj4w_IMG_0250.jpg"
                alt="Baron Davis"
                className="w-24 h-24 rounded-full object-cover ring-2 ring-white/10"
              />
              <div className="text-center md:text-left">
                <p className="serif-heading text-[22px] md:text-[24px] text-white/90 italic leading-relaxed mb-4">
                  "<strong className="font-semibold not-italic">Ramon Govea</strong> knows how to take your <strong className="font-semibold not-italic">content</strong> to the next level."
                </p>
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <span className="text-[13px] font-semibold tracking-[0.12em] text-white/80">BARON DAVIS</span>
                  <div className="flex justify-center md:justify-start">
                    {[1,2,3,4].map(i => <Star key={i} size={14} fill="#C6A85E" className="text-[#C6A85E]" />)}
                  </div>
                </div>
                <p className="text-[13px] text-white/50 mt-1 tracking-wide">NBA All-Star & Investor</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/[0.06] text-center">
        <p className="text-[14px] font-semibold tracking-[0.12em] text-white/80 mb-5">RAMON GOVEA</p>
        <div className="flex justify-center gap-6 text-[13px] text-white/50 mb-5">
          <a href="/#work" className="hover:text-white/80 transition-colors duration-200">Work</a>
          <span className="text-white/20">•</span>
          <a href="/#about" className="hover:text-white/80 transition-colors duration-200">About</a>
          <span className="text-white/20">•</span>
          <a href="/#contact" className="hover:text-white/80 transition-colors duration-200">Contact</a>
          <span className="text-white/20">•</span>
          <a href="#" className="hover:text-white/80 transition-colors duration-200">Terms</a>
        </div>
        <p className="text-[11px] text-white/40 tracking-wide">© Ramon Govea. All rights Reserved. · <a href="#" className="underline hover:text-white/60">Privacy Policy</a> · Terms</p>
      </footer>
    </div>
  );
};

// Quiz Component
const Quiz = ({ onComplete, answers, setAnswers }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [textAnswer, setTextAnswer] = useState('');

  const question = quizQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;

  const handleAnswer = (answer) => {
    const newAnswers = { ...answers, [`q${question.id}`]: answer };
    setAnswers(newAnswers);
    
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setTextAnswer('');
    } else {
      onComplete(newAnswers);
    }
  };

  const handleTextSubmit = () => {
    if (textAnswer.trim()) {
      handleAnswer(textAnswer.trim());
    }
  };

  // Update Q4 question text based on Q3 answer
  const getQuestionText = () => {
    if (question.id === 4 && answers.q3) {
      const q3Map = {
        "Don't know how to pitch/package it properly": "not knowing how to pitch/package your IP",
        "No connections to studios/investors": "lacking connections to studios/investors",
        "Can't afford traditional development costs": "traditional development costs being too high",
        "Don't understand how to use AI to accelerate production": "not understanding how to use AI",
        "Not sure if my IP is actually franchiseable": "being unsure if your IP is franchiseable"
      };
      return `When you think about why ${q3Map[answers.q3] || "that"} keeps you stuck, what's really going on?`;
    }
    return question.question;
  };

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white flex flex-col">
      {/* Progress Bar */}
      <div className="px-6 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Question {currentQuestion + 1} of {quizQuestions.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#c9a962] to-[#d4b978] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl w-full">
          <h2 className="text-2xl md:text-3xl font-serif text-center mb-10">
            {getQuestionText()}
          </h2>

          {question.type === 'multiple' ? (
            <div className="space-y-4">
              {question.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(option)}
                  className="w-full text-left p-5 border border-white/20 rounded-lg hover:border-[#c9a962] hover:bg-white/5 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center group-hover:border-[#c9a962] group-hover:bg-[#c9a962]/10 transition-colors">
                      <span className="text-sm">{String.fromCharCode(65 + index)}</span>
                    </div>
                    <span className="text-gray-200">{option}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              <textarea
                value={textAnswer}
                onChange={(e) => setTextAnswer(e.target.value)}
                placeholder={question.placeholder}
                className="w-full p-5 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-[#c9a962] min-h-[150px] resize-none"
                maxLength={500}
              />
              <button
                onClick={handleTextSubmit}
                disabled={!textAnswer.trim()}
                className="w-full bg-gradient-to-r from-[#c9a962] to-[#d4b978] text-black py-4 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg hover:shadow-[#c9a962]/30"
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Email Capture Component
const EmailCapture = ({ onSubmit }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    setIsSubmitting(true);
    setError('');
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    onSubmit(email);
  };

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#c9a962] to-[#d4b978] flex items-center justify-center mx-auto mb-8">
          <Check size={40} className="text-black" />
        </div>
        <h2 className="text-3xl font-serif mb-4">Almost There!</h2>
        <p className="text-gray-300 mb-8">
          Enter your email to see your personalized results and franchise roadmap
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="w-full p-4 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-[#c9a962]"
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-[#c9a962] to-[#d4b978] text-black py-4 font-semibold disabled:opacity-70 transition-all hover:shadow-lg hover:shadow-[#c9a962]/30"
          >
            {isSubmitting ? 'Processing...' : 'See My Results'}
          </button>
        </form>
        <p className="text-xs text-gray-500 mt-4">
          We respect your privacy. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
};

// Results Page A - Strategic Gap
const ResultsPageA = ({ answers, onBuyBook, onBuyCourse }) => {
  return (
    <div className="min-h-screen bg-[#0a0a12] text-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <p className="text-[#c9a962] uppercase tracking-widest mb-4">Your Results</p>
          <h1 className="text-3xl md:text-4xl font-serif mb-4">
            You Have the IP — You're Missing the Franchise Strategy
          </h1>
          <p className="text-gray-400">Based on your answers, here's what's holding you back...</p>
        </div>

        {/* Mirror Q4 Answer */}
        {answers.q4 && (
          <div className="bg-white/5 border-l-4 border-[#c9a962] p-6 mb-8 italic text-gray-300">
            "{answers.q4}"
          </div>
        )}

        {/* Analysis */}
        <div className="prose prose-invert max-w-none mb-12">
          <h3 className="text-xl font-semibold text-white mb-4">Here's what's really happening:</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            You're stuck in the trap most creative founders fall into — treating your IP like a single project instead of a scalable business asset. Without a clear franchise bible, monetization roadmap, and pitch-ready materials, studios and investors can't see the full potential of what you're building. They need to understand how your world expands beyond one story — into sequels, spin-offs, games, merch, licensing deals.
          </p>
          <p className="text-gray-300 leading-relaxed mb-4">
            The traditional path to developing this infrastructure costs $50K-100K and takes 6-12 months with agencies and consultants. That's why 90% of promising IP never makes it past the concept phase. Creators give up or settle for one-off deals that leave money on the table.
          </p>
          <p className="text-gray-300 leading-relaxed">
            <strong className="text-white">The breakthrough:</strong> AI workflows now let you create franchise bibles, pitch decks, and proof-of-concept materials for under $200 in a matter of days — but only if you have the right strategic framework first. Without it, you're just using innovative tools to create the wrong things faster.
          </p>
        </div>

        {/* Primary Offer */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-xl p-8 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-[#c9a962] text-black text-xs px-3 py-1 rounded-full font-semibold">START HERE</span>
          </div>
          <h3 className="text-2xl font-serif mb-2">Story Framework Formula</h3>
          <p className="text-3xl font-bold text-[#c9a962] mb-4">$17</p>
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <Check size={20} className="text-[#c9a962] mt-0.5" />
              <span className="text-gray-300">The 5-part framework that transforms ideas into franchise-ready IP</span>
            </li>
            <li className="flex items-start gap-3">
              <Check size={20} className="text-[#c9a962] mt-0.5" />
              <span className="text-gray-300">Templates for world-building, character arcs, and expansion mapping</span>
            </li>
            <li className="flex items-start gap-3">
              <Check size={20} className="text-[#c9a962] mt-0.5" />
              <span className="text-gray-300">Instant access — start implementing today</span>
            </li>
          </ul>
          <button
            onClick={onBuyBook}
            className="w-full bg-gradient-to-r from-[#c9a962] to-[#d4b978] text-black py-4 font-semibold transition-all hover:shadow-lg hover:shadow-[#c9a962]/30"
          >
            Get Story Framework Formula ($17)
          </button>
        </div>

        {/* Upgrade Offer */}
        <div className="border border-white/10 rounded-xl p-8 text-center">
          <p className="text-gray-400 uppercase tracking-widest text-sm mb-4">Ready to Go Deeper?</p>
          <h3 className="text-2xl font-serif mb-2">AI for Creatives Course</h3>
          <p className="text-2xl font-bold text-white mb-4">$197</p>
          <p className="text-gray-400 mb-6">
            Complete system for building franchise-ready IP with AI workflows
          </p>
          <button
            onClick={onBuyCourse}
            className="border border-[#c9a962] text-[#c9a962] px-8 py-3 font-semibold hover:bg-[#c9a962]/10 transition-colors"
          >
            Enroll in Full Course ($197)
          </button>
        </div>
      </div>
    </div>
  );
};

// Results Page B - Technical Gap
const ResultsPageB = ({ answers, onBuyCourse }) => {
  const [tier, setTier] = useState('standard');
  
  return (
    <div className="min-h-screen bg-[#0a0a12] text-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <p className="text-[#c9a962] uppercase tracking-widest mb-4">Your Results</p>
          <h1 className="text-3xl md:text-4xl font-serif mb-4">
            You Understand Strategy — You Need AI Workflows to Execute at Speed
          </h1>
          <p className="text-gray-400">Based on your answers, here's what's holding you back...</p>
        </div>

        {answers.q4 && (
          <div className="bg-white/5 border-l-4 border-[#c9a962] p-6 mb-8 italic text-gray-300">
            "{answers.q4}"
          </div>
        )}

        <div className="prose prose-invert max-w-none mb-12">
          <h3 className="text-xl font-semibold text-white mb-4">Here's what's actually holding you back:</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            You see other creators moving at 10x speed with AI tools, but the landscape is overwhelming. Every week there's a new "must-have" tool that promises to revolutionize your workflow. Most tutorials teach isolated tricks — how to generate one image, write one script — but nobody's showing you the complete production pipeline that turns concepts into finished, pitch-ready IP assets.
          </p>
          <p className="text-gray-300 leading-relaxed mb-4">
            The result? You waste time experimenting with tools that don't integrate with each other, or worse — you create impressive-looking outputs that don't actually serve your franchise goals. A cool AI-generated character design means nothing if it's not consistent across 50 scenes, or if it doesn't fit your world's aesthetic logic.
          </p>
          <p className="text-gray-300 leading-relaxed">
            <strong className="text-white">What you actually need:</strong> A systematic workflow that chains the right tools together in the right sequence — from world-building to character consistency to animatics to pitch materials. The tools are cheaper and more powerful than ever, but without the workflow architecture, you're just burning time and money on shiny objects instead of building real IP equity.
          </p>
        </div>

        {/* Tier Toggle */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setTier('standard')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${tier === 'standard' ? 'bg-[#c9a962] text-black' : 'border border-white/20 text-white hover:border-white/40'}`}
          >
            Standard — $197
          </button>
          <button
            onClick={() => setTier('vip')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${tier === 'vip' ? 'bg-[#c9a962] text-black' : 'border border-white/20 text-white hover:border-white/40'}`}
          >
            VIP — $497
          </button>
        </div>

        {/* Course Offer */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-xl p-8 mb-8">
          <h3 className="text-2xl font-serif mb-2">AI for Creatives Course</h3>
          <p className="text-3xl font-bold text-[#c9a962] mb-4">
            {tier === 'standard' ? '$197' : '$497'}
          </p>
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <Check size={20} className="text-[#c9a962] mt-0.5" />
              <span className="text-gray-300">Complete AI production workflow from concept to pitch materials</span>
            </li>
            <li className="flex items-start gap-3">
              <Check size={20} className="text-[#c9a962] mt-0.5" />
              <span className="text-gray-300">Character consistency frameworks for visual IP development</span>
            </li>
            <li className="flex items-start gap-3">
              <Check size={20} className="text-[#c9a962] mt-0.5" />
              <span className="text-gray-300">Tool-agnostic systems that work as AI evolves</span>
            </li>
            {tier === 'vip' && (
              <li className="flex items-start gap-3">
                <Check size={20} className="text-[#c9a962] mt-0.5" />
                <span className="text-gray-300"><strong>VIP:</strong> 30-minute Strategy Audit call included</span>
              </li>
            )}
          </ul>
          <button
            onClick={() => onBuyCourse(tier)}
            className="w-full bg-gradient-to-r from-[#c9a962] to-[#d4b978] text-black py-4 font-semibold transition-all hover:shadow-lg hover:shadow-[#c9a962]/30"
          >
            Enroll Now ({tier === 'standard' ? '$197' : '$497'})
          </button>
        </div>

        {/* Downgrade */}
        <p className="text-center text-gray-400">
          Prefer to start smaller? <a href="/ai-for-creatives/checkout/book" className="text-[#c9a962] underline">Get the Story Framework Formula for $17</a>
        </p>
      </div>
    </div>
  );
};

// Results Page C - Execution Gap
const ResultsPageC = ({ answers, onBookAudit }) => {
  const nextDate = new Date();
  nextDate.setDate(nextDate.getDate() + 7);
  const dateString = nextDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <p className="text-[#c9a962] uppercase tracking-widest mb-4">Your Results</p>
          <h1 className="text-3xl md:text-4xl font-serif mb-4">
            Your IP Is Ready — You Need Expert Guidance to Take It to Market
          </h1>
          <p className="text-gray-400">Based on your answers, here's what's holding you back...</p>
        </div>

        {answers.q4 && (
          <div className="bg-white/5 border-l-4 border-[#c9a962] p-6 mb-8 italic text-gray-300">
            "{answers.q4}"
          </div>
        )}

        <div className="prose prose-invert max-w-none mb-12">
          <h3 className="text-xl font-semibold text-white mb-4">Here's the real issue:</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            Your IP is developed enough to have value, but you're stuck at the gate because you don't have the industry relationships or proven playbook to get it in front of the right decision-makers. You've seen other creators with arguably weaker concepts land deals while yours sits on the shelf — and it's frustrating as hell.
          </p>
          <p className="text-gray-300 leading-relaxed mb-4">
            <strong className="text-white">The brutal truth:</strong> Pitch quality matters less than you think. Studios and investors see hundreds of polished decks every month. What actually gets deals done is strategic positioning — knowing which doors to knock on, how to frame your IP for different buyers (streaming vs. gaming vs. licensing), and having the credibility markers that make gatekeepers take you seriously instead of sending a form rejection.
          </p>
          <p className="text-gray-300 leading-relaxed">
            This isn't something you can learn from a course or figure out through trial and error. You need someone who's been inside these rooms, knows what buyers actually care about (hint: it's not your hero's journey structure), and can audit your specific IP to identify the highest-probability monetization paths based on current market conditions — not 2019's playbook that everyone's still teaching.
          </p>
        </div>

        {/* Audit Offer */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-xl p-8 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-red-500/20 text-red-400 text-xs px-3 py-1 rounded-full font-semibold">LIMITED SPOTS</span>
          </div>
          <h3 className="text-2xl font-serif mb-2">90-Minute Strategy Audit</h3>
          <p className="text-3xl font-bold text-[#c9a962] mb-2">$497</p>
          <p className="text-sm text-gray-400 mb-6">Next opening: {dateString}</p>
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <Check size={20} className="text-[#c9a962] mt-0.5" />
              <span className="text-gray-300">Deep-dive analysis of your IP's franchise potential</span>
            </li>
            <li className="flex items-start gap-3">
              <Check size={20} className="text-[#c9a962] mt-0.5" />
              <span className="text-gray-300">Identify highest-probability monetization paths</span>
            </li>
            <li className="flex items-start gap-3">
              <Check size={20} className="text-[#c9a962] mt-0.5" />
              <span className="text-gray-300">Custom roadmap based on current market conditions</span>
            </li>
            <li className="flex items-start gap-3">
              <Check size={20} className="text-[#c9a962] mt-0.5" />
              <span className="text-gray-300">Specific intro recommendations where relevant</span>
            </li>
          </ul>
          <button
            onClick={onBookAudit}
            className="w-full bg-gradient-to-r from-[#c9a962] to-[#d4b978] text-black py-4 font-semibold transition-all hover:shadow-lg hover:shadow-[#c9a962]/30"
          >
            Book Strategy Audit ($497)
          </button>
        </div>

        {/* Downgrade */}
        <p className="text-center text-gray-400">
          Not ready for 1-on-1? <a href="/ai-for-creatives/checkout/course" className="text-[#c9a962] underline">Start with AI for Creatives Course ($197)</a>
        </p>
      </div>
    </div>
  );
};

// Main Funnel Component
const AIForCreatives = () => {
  const [step, setStep] = useState('landing'); // landing, quiz, email, results
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [resultsPage, setResultsPage] = useState('A');

  const handleStartQuiz = () => {
    setStep('quiz');
  };

  const handleQuizComplete = (quizAnswers) => {
    setAnswers(quizAnswers);
    setStep('email');
  };

  const handleEmailSubmit = async (submittedEmail) => {
    setEmail(submittedEmail);
    
    // Save to backend
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quiz-submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: submittedEmail, answers })
      });
      if (!response.ok) console.error('Failed to save quiz submission');
    } catch (error) {
      console.error('Error saving quiz submission:', error);
    }

    // Determine routing based on Q3 answer
    const q3Answer = answers.q3;
    if (q3Answer === "Don't know how to pitch/package it properly" || 
        q3Answer === "Not sure if my IP is actually franchiseable") {
      setResultsPage('A');
    } else if (q3Answer === "Don't understand how to use AI to accelerate production" || 
               q3Answer === "Can't afford traditional development costs") {
      setResultsPage('B');
    } else if (q3Answer === "No connections to studios/investors") {
      setResultsPage('C');
    } else {
      setResultsPage('A'); // Default
    }
    
    setStep('results');
  };

  const navigateToCheckout = (product, tier = 'standard') => {
    window.location.href = `/ai-for-creatives/checkout/${product}${tier !== 'standard' ? `?tier=${tier}` : ''}`;
  };

  if (step === 'landing') {
    return <LandingPage onStartQuiz={handleStartQuiz} />;
  }

  if (step === 'quiz') {
    return <Quiz onComplete={handleQuizComplete} answers={answers} setAnswers={setAnswers} />;
  }

  if (step === 'email') {
    return <EmailCapture onSubmit={handleEmailSubmit} />;
  }

  if (step === 'results') {
    if (resultsPage === 'A') {
      return (
        <ResultsPageA 
          answers={answers} 
          onBuyBook={() => navigateToCheckout('book')}
          onBuyCourse={() => navigateToCheckout('course')}
        />
      );
    }
    if (resultsPage === 'B') {
      return (
        <ResultsPageB 
          answers={answers} 
          onBuyCourse={(tier) => navigateToCheckout('course', tier)}
        />
      );
    }
    if (resultsPage === 'C') {
      return (
        <ResultsPageC 
          answers={answers} 
          onBookAudit={() => navigateToCheckout('audit')}
        />
      );
    }
  }

  return null;
};

export default AIForCreatives;
